import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Dialog } from "@/components/ui/dialog";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useExtensions, useCreateExtension, useUpdateExtension, useDeleteExtension } from "@/hooks/use-extensions";
import { useContexts } from "@/hooks/use-contexts";
import { useEndpoints } from "@/hooks/use-endpoints";
import { useWebsocketEndpoints } from "@/hooks/use-websocket-endpoints";
import { useAriApps } from "@/hooks/use-ari-apps";
import type { DialplanExtension, ActionType, ActionCreate } from "@/types";
import { CODECS } from "@/types";
import { Plus, Trash2, ChevronDown, ChevronUp, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

const ACTION_TYPES: { value: ActionType; label: string }[] = [
  { value: "answer", label: "Answer" },
  { value: "hangup", label: "Hangup" },
  { value: "dial", label: "Dial" },
  { value: "voicemail", label: "Voicemail" },
  { value: "set", label: "Set" },
  { value: "goto", label: "Goto" },
  { value: "stasis", label: "Stasis" },
  { value: "playback", label: "Playback" },
  { value: "noop", label: "NoOp" },
  { value: "verbose", label: "Verbose" },
  { value: "wait", label: "Wait" },
  { value: "queue", label: "Queue" },
  { value: "mixmonitor", label: "MixMonitor" },
  { value: "return", label: "Return" },
  { value: "bridge", label: "Bridge" },
  { value: "echo", label: "Echo" },
  { value: "record", label: "Record" },
  { value: "sipaddheader", label: "SIP Add Header" },
  { value: "senddtmf", label: "SendDTMF" },
];

function ActionForm({ action, onChange, onRemove, onMoveUp, onMoveDown, total, index, endpoints, wsEndpoints, ariApps, contexts }: {
  action: ActionCreate;
  onChange: (a: ActionCreate) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  total: number;
  index: number;
  endpoints: { name: string }[];
  wsEndpoints: { name: string }[];
  ariApps: { name: string }[];
  contexts: { name: string }[];
}) {
  const [expanded, setExpanded] = useState(true);
  const params = action.params as Record<string, unknown>;

  const updateType = (type: ActionType) => {
    let defaults: Record<string, unknown> = {};
    if (type === "dial") defaults = { target_type: "pjsip", endpoint_name: "", timeout: 30, flags: "" };
    if (type === "senddtmf") defaults = { digits: "", timeout_ms: 250, duration_ms: 0, channel: "", options: "" };
    onChange({ ...action, type, params: defaults });
  };

  const updateParam = (key: string, value: unknown) => {
    onChange({ ...action, params: { ...params, [key]: value } });
  };

  return (
    <Card className="bg-secondary/30">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex flex-col gap-0.5">
            {onMoveUp && <Button variant="ghost" size="icon" className="h-4 w-4" onClick={onMoveUp} disabled={index === 0}><ArrowUp size={10} /></Button>}
            {onMoveDown && <Button variant="ghost" size="icon" className="h-4 w-4" onClick={onMoveDown} disabled={index === total - 1}><ArrowDown size={10} /></Button>}
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
          <div className="flex-1 grid grid-cols-2 gap-2">
            <div className="space-y-0.5">
              <Label className="text-[10px]">Priority <HelpIcon text="Execution order (1, 2, 3...). Lower numbers run first. Must be unique per extension." /></Label>
              <Input type="number" value={action.priority} onChange={(e) => onChange({ ...action, priority: Number(e.target.value) })} className="h-7 text-xs" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[10px]">Action Type <HelpIcon text="The dialplan application to execute. Answer = answer call. Dial = ring endpoint. Hangup = terminate." /></Label>
              <Select value={action.type} onChange={(e) => updateType(e.target.value as ActionType)} className="h-7 text-xs">
                {ACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={onRemove}>
            <Trash2 size={14} />
          </Button>
        </div>

        {expanded && <ActionParams action={action} updateParam={updateParam} endpoints={endpoints} wsEndpoints={wsEndpoints} ariApps={ariApps} contexts={contexts} />}
      </CardContent>
    </Card>
  );
}

function ActionParams({ action, updateParam, endpoints, wsEndpoints, ariApps, contexts }: {
  action: ActionCreate;
  updateParam: (key: string, value: unknown) => void;
  endpoints: { name: string }[];
  wsEndpoints: { name: string }[];
  ariApps: { name: string }[];
  contexts: { name: string }[];
}) {
  const params = action.params as Record<string, unknown>;

  switch (action.type) {
    case "answer":
      return (
        <div className="grid grid-cols-1 gap-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Delay (ms) <HelpIcon text="Milliseconds to wait before answering. Default 0. Set > 0 for caller ID lookup." /></Label>
            <Input type="number" value={(params.delay as number) ?? 0} onChange={(e) => updateParam("delay", Number(e.target.value))} className="h-7 text-xs" />
          </div>
        </div>
      );
    case "hangup":
      return (
        <div className="space-y-0.5">
          <Label className="text-[10px]">Cause <HelpIcon text="Hangup cause code. 16 = normal clearing, 17 = busy, 21 = rejected. Empty = normal." /></Label>
          <Input value={(params.cause as string) ?? ""} onChange={(e) => updateParam("cause", e.target.value)} className="h-7 text-xs" />
        </div>
      );
    case "dial":
      return (
        <div className="space-y-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Target Type <HelpIcon text="PJSIP for standard SIP endpoints. WebSocket for AI/STT service connections." /></Label>
            <Select value={(params.target_type as string) ?? "pjsip"} onChange={(e) => updateParam("target_type", e.target.value)} className="h-7 text-xs">
              <option value="pjsip">PJSIP</option>
              <option value="websocket">WebSocket</option>
            </Select>
          </div>
          {(params.target_type as string) === "websocket" ? (
            <>
              <div className="space-y-0.5">
                <Label className="text-[10px]">WebSocket Connection * <HelpIcon text="The WebSocket client endpoint to dial. Required for WebSocket routing." /></Label>
                <Select value={(params.connection_name as string) ?? ""} onChange={(e) => updateParam("connection_name", e.target.value)} className="h-7 text-xs">
                  <option value="">Select...</option>
                  {wsEndpoints.map((w) => <option key={w.name} value={w.name}>{w.name}</option>)}
                </Select>
              </div>
              <div className="space-y-0.5">
                <Label className="text-[10px]">Codec <HelpIcon text="Audio codec for WebSocket calls. Asterisk negotiates best match." /></Label>
                <Select value={(params.codecs as string) ?? "ulaw"} onChange={(e) => updateParam("codecs", e.target.value)} className="h-7 text-xs">
                  {CODECS.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </div>
              <div className="space-y-0.5">
                <Label className="text-[10px]">Control Format <HelpIcon text="Control message format sent over the WebSocket. json is default." /></Label>
                <Select value={(params.control_format as string) ?? "json"} onChange={(e) => updateParam("control_format", e.target.value)} className="h-7 text-xs">
                  <option value="json">JSON</option>
                  <option value="plain-text">Plain Text</option>
                </Select>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px]">URI Parameters <HelpIcon text="Query parameters appended to the WebSocket dial string as URI-encoded key=value pairs." /></Label>
                  <Button type="button" variant="ghost" size="sm" className="h-5 text-[10px]" onClick={() => {
                    const existing = (params.query_params as Array<{ key: string; value: string }>) ?? [];
                    updateParam("query_params", [...existing, { key: "", value: "" }]);
                  }}>
                    <Plus size={10} /> Add Query Param
                  </Button>
                </div>
                {((params.query_params as Array<{ key: string; value: string }>) ?? []).map((qp, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <Input value={qp.key} onChange={(e) => {
                      const qps = [...((params.query_params as Array<{ key: string; value: string }>) ?? [])];
                      qps[i] = { ...qps[i], key: e.target.value };
                      updateParam("query_params", qps);
                    }} placeholder="key" className="h-7 text-xs flex-1" />
                    <Input value={qp.value} onChange={(e) => {
                      const qps = [...((params.query_params as Array<{ key: string; value: string }>) ?? [])];
                      qps[i] = { ...qps[i], value: e.target.value };
                      updateParam("query_params", qps);
                    }} placeholder="value" className="h-7 text-xs flex-1" />
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => {
                      const qps = ((params.query_params as Array<{ key: string; value: string }>) ?? []).filter((_, idx) => idx !== i);
                      updateParam("query_params", qps);
                    }}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-0.5">
              <Label className="text-[10px]">Endpoint <HelpIcon text="The PJSIP endpoint name to dial. Must be a pre-configured endpoint." /></Label>
              <Select value={(params.endpoint_name as string) ?? ""} onChange={(e) => updateParam("endpoint_name", e.target.value)} className="h-7 text-xs">
                <option value="">Select...</option>
                {endpoints.map((ep) => <option key={ep.name} value={ep.name}>{ep.name}</option>)}
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-0.5">
              <Label className="text-[10px]">Timeout <HelpIcon text="Maximum ring time in seconds before the dial attempt fails. Default 30." /></Label>
              <Input type="number" value={(params.timeout as number) ?? 30} onChange={(e) => updateParam("timeout", Number(e.target.value))} className="h-7 text-xs" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[10px]">Flags <HelpIcon text="Dial option flags. t = allow callee transfer, T = allow caller transfer, m = music on hold." /></Label>
              <Input value={(params.flags as string) ?? ""} onChange={(e) => updateParam("flags", e.target.value)} className="h-7 text-xs" />
            </div>
          </div>
        </div>
      );
    case "voicemail":
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Mailbox * <HelpIcon text="Voicemail mailbox identifier. Format: mailbox@context. Example: 1001@default." /></Label>
            <Input value={(params.mailbox as string) ?? ""} onChange={(e) => updateParam("mailbox", e.target.value)} className="h-7 text-xs" />
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px]">Flags</Label>
            <Input value={(params.flags as string) ?? ""} onChange={(e) => updateParam("flags", e.target.value)} className="h-7 text-xs" />
          </div>
        </div>
      );
    case "set":
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Variable * <HelpIcon text="Channel variable name to set. Example: CALLERID(name). Variables persist for the call." /></Label>
            <Input value={(params.variable as string) ?? ""} onChange={(e) => updateParam("variable", e.target.value)} className="h-7 text-xs" />
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px]">Value <HelpIcon text="Value to assign to the variable. Example: John Doe." /></Label>
            <Input value={(params.value as string) ?? ""} onChange={(e) => updateParam("value", e.target.value)} className="h-7 text-xs" />
          </div>
        </div>
      );
    case "goto":
      return (
        <div className="space-y-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Context * <HelpIcon text="Target dialplan context to jump to. Must be an existing context." /></Label>
            <Select value={(params.context as string) ?? ""} onChange={(e) => updateParam("context", e.target.value)} className="h-7 text-xs">
              <option value="">Select...</option>
              {contexts.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-0.5">
              <Label className="text-[10px]">Extension * <HelpIcon text="Target extension within the context. Example: 100 or s." /></Label>
              <Input value={(params.extension as string) ?? ""} onChange={(e) => updateParam("extension", e.target.value)} className="h-7 text-xs" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[10px]">Priority <HelpIcon text="Target priority within the extension. Default 1 = start from beginning." /></Label>
              <Input type="number" value={(params.priority as number) ?? 1} onChange={(e) => updateParam("priority", Number(e.target.value))} className="h-7 text-xs" />
            </div>
          </div>
        </div>
      );
    case "stasis":
      return (
        <div className="space-y-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">ARI App * <HelpIcon text="ARI application name to hand the call to. Must be registered in ARI Apps." /></Label>
            <Select value={(params.app_name as string) ?? ""} onChange={(e) => updateParam("app_name", e.target.value)} className="h-7 text-xs">
              <option value="">Select...</option>
              {ariApps.map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
            </Select>
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px]">Args <HelpIcon text="Arguments passed to the ARI application on StasisStart event." /></Label>
            <Input value={(params.args as string) ?? ""} onChange={(e) => updateParam("args", e.target.value)} className="h-7 text-xs" />
          </div>
        </div>
      );
    case "playback":
      return (
        <div className="space-y-0.5">
          <Label className="text-[10px]">Filename * <HelpIcon text="Audio file to play. Must exist in Asterisk sounds directory. Example: hello-world." /></Label>
          <Input value={(params.filename as string) ?? ""} onChange={(e) => updateParam("filename", e.target.value)} className="h-7 text-xs" />
        </div>
      );
    case "noop":
      return (
        <div className="space-y-0.5">
          <Label className="text-[10px]">Text <HelpIcon text="Text to display in Asterisk console when this NoOp action executes. Useful for debugging." /></Label>
          <Input value={(params.text as string) ?? ""} onChange={(e) => updateParam("text", e.target.value)} className="h-7 text-xs" />
        </div>
      );
    case "verbose":
      return (
        <div className="space-y-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Level (0-4) <HelpIcon text="Verbosity level. 0 = always shown. Higher numbers only at matching verbosity. Default 0." /></Label>
            <Input type="number" min={0} max={4} value={(params.level as number) ?? 0} onChange={(e) => updateParam("level", Number(e.target.value))} className="h-7 text-xs" />
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px]">Message * <HelpIcon text="Message to log to Asterisk console." /></Label>
            <Input value={(params.message as string) ?? ""} onChange={(e) => updateParam("message", e.target.value)} className="h-7 text-xs" />
          </div>
        </div>
      );
    case "wait":
      return (
        <div className="space-y-0.5">
          <Label className="text-[10px]">Seconds * <HelpIcon text="Pause duration. Supports decimals: 0.5 = half second. Useful for timing-dependent flows." /></Label>
          <Input type="number" step="0.1" value={(params.seconds as number) ?? 1} onChange={(e) => updateParam("seconds", Number(e.target.value))} className="h-7 text-xs" />
        </div>
      );
    case "queue":
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Queue Name * <HelpIcon text="Call queue identifier. Must match a queue defined in queues.conf." /></Label>
            <Input value={(params.queue_name as string) ?? ""} onChange={(e) => updateParam("queue_name", e.target.value)} className="h-7 text-xs" />
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px]">Options <HelpIcon text="Application-specific options. See Asterisk docs for available flags per application." /></Label>
            <Input value={(params.options as string) ?? ""} onChange={(e) => updateParam("options", e.target.value)} className="h-7 text-xs" />
          </div>
        </div>
      );
    case "mixmonitor":
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Filename * <HelpIcon text="Audio file name for recording. Can include variables like ${UNIQUEID}. Example: /var/lib/asterisk/sounds/rec-${UNIQUEID}." /></Label>
            <Input value={(params.filename as string) ?? ""} onChange={(e) => updateParam("filename", e.target.value)} className="h-7 text-xs" />
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px]">Options</Label>
            <Input value={(params.options as string) ?? ""} onChange={(e) => updateParam("options", e.target.value)} className="h-7 text-xs" />
          </div>
        </div>
      );
    case "return":
      return (
        <div className="space-y-0.5">
          <Label className="text-[10px]">Value</Label>
          <Input value={(params.value as string) ?? ""} onChange={(e) => updateParam("value", e.target.value)} className="h-7 text-xs" />
        </div>
      );
    case "bridge":
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Channel * <HelpIcon text="Channel to bridge to. Format: PJSIP/endpoint-name or SIP/uri." /></Label>
            <Input value={(params.channel as string) ?? ""} onChange={(e) => updateParam("channel", e.target.value)} className="h-7 text-xs" />
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px]">Options</Label>
            <Input value={(params.options as string) ?? ""} onChange={(e) => updateParam("options", e.target.value)} className="h-7 text-xs" />
          </div>
        </div>
      );
    case "echo":
      return <p className="text-xs text-muted-foreground">No parameters needed.</p>;
    case "record":
      return (
        <div className="space-y-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Filename * <HelpIcon text="Recording filename. Can include variables like ${UNIQUEID}. Example: /var/lib/asterisk/sounds/rec-${UNIQUEID}." /></Label>
            <Input value={(params.filename as string) ?? ""} onChange={(e) => updateParam("filename", e.target.value)} className="h-7 text-xs" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-0.5">
              <Label className="text-[10px]">Silence <HelpIcon text="Seconds of silence before auto-stopping recording. 0 = no auto-stop (record until hangup)." /></Label>
              <Input type="number" value={(params.silence as number) ?? 0} onChange={(e) => updateParam("silence", Number(e.target.value))} className="h-7 text-xs" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[10px]">Max Duration <HelpIcon text="Maximum recording length in seconds. 0 = unlimited. Recording stops after this duration." /></Label>
              <Input type="number" value={(params.maxduration as number) ?? 0} onChange={(e) => updateParam("maxduration", Number(e.target.value))} className="h-7 text-xs" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[10px]">Options</Label>
              <Input value={(params.options as string) ?? ""} onChange={(e) => updateParam("options", e.target.value)} className="h-7 text-xs" />
            </div>
          </div>
        </div>
      );
    case "sipaddheader":
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Header * <HelpIcon text="SIP header name to add. Example: X-Custom-ID." /></Label>
            <Input value={(params.header as string) ?? ""} onChange={(e) => updateParam("header", e.target.value)} className="h-7 text-xs" />
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px]">Content * <HelpIcon text="SIP header value. Example: call-12345." /></Label>
            <Input value={(params.content as string) ?? ""} onChange={(e) => updateParam("content", e.target.value)} className="h-7 text-xs" />
          </div>
        </div>
      );
    case "senddtmf":
      return (
        <div className="space-y-2">
          <div className="space-y-0.5">
            <Label className="text-[10px]">Digits * <HelpIcon text="List of digits 0-9,*#,a-d,A-D to send. Also w (0.5s pause), W (1s pause), f/F (flash-hook)." /></Label>
            <Input value={(params.digits as string) ?? ""} onChange={(e) => updateParam("digits", e.target.value)} className="h-7 text-xs" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-0.5">
              <Label className="text-[10px]">Timeout (ms) <HelpIcon text="Amount of time to wait in ms between tones. Default 250." /></Label>
              <Input type="number" value={(params.timeout_ms as number) ?? 250} onChange={(e) => updateParam("timeout_ms", Number(e.target.value))} className="h-7 text-xs" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[10px]">Duration (ms) <HelpIcon text="Duration of each digit in ms." /></Label>
              <Input type="number" value={(params.duration_ms as number) ?? 0} onChange={(e) => updateParam("duration_ms", Number(e.target.value))} className="h-7 text-xs" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-0.5">
              <Label className="text-[10px]">Channel <HelpIcon text="Channel where digits will be played. Leave empty for current channel." /></Label>
              <Input value={(params.channel as string) ?? ""} onChange={(e) => updateParam("channel", e.target.value)} className="h-7 text-xs" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[10px]">Options <HelpIcon text="a = Answer the channel if not already up." /></Label>
              <Input value={(params.options as string) ?? ""} onChange={(e) => updateParam("options", e.target.value)} className="h-7 text-xs" />
            </div>
          </div>
        </div>
      );
    default:
      return null;
  }
}

const columns: Column<DialplanExtension>[] = [
  { key: "extension", header: "Extension" },
  {
    key: "context_id",
    header: "Context",
    render: (e) => <span className="font-mono text-xs">{e.context_id.slice(0, 8)}...</span>,
  },
  {
    key: "actions",
    header: "Actions",
    render: (e) => <div className="flex flex-wrap gap-1">{(e.actions || []).map((a) => <Badge key={a.id} variant="secondary" className="text-[10px]">{a.application} p{a.priority}</Badge>)}</div>,
  },
];

export default function ExtensionsPage() {
  const [searchParams] = useSearchParams();
  const ctxFromUrl = searchParams.get("context_id") ?? "";

  const { data: allExtensions, isLoading } = useExtensions();
  const { data: contexts } = useContexts();
  const { data: endpoints } = useEndpoints();
  const { data: wsEndpoints } = useWebsocketEndpoints();
  const { data: ariApps } = useAriApps();
  const createMut = useCreateExtension();
  const updateMut = useUpdateExtension();
  const deleteMut = useDeleteExtension();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DialplanExtension | null>(null);
  const [formCtxId, setFormCtxId] = useState("");
  const [formExtension, setFormExtension] = useState("");
  const [actions, setActions] = useState<ActionCreate[]>([]);

  const endpointNames = useMemo(() => endpoints?.map((e) => ({ name: e.name })) ?? [], [endpoints]);
  const wsNames = useMemo(() => wsEndpoints?.map((w) => ({ name: w.name })) ?? [], [wsEndpoints]);
  const ariNames = useMemo(() => ariApps?.map((a) => ({ name: a.name })) ?? [], [ariApps]);
  const contextNames = useMemo(() => contexts?.map((c) => ({ name: c.name })) ?? [], [contexts]);

  const [filterCtx, setFilterCtx] = useState(ctxFromUrl);
  const filtered = allExtensions?.filter((e) => !filterCtx || e.context_id === filterCtx) ?? [];

  const openCreate = () => {
    setEditing(null);
    setFormCtxId("");
    setFormExtension("");
    setActions([{ priority: 1, type: "answer", params: { delay: 0 } }]);
    setOpen(true);
  };

  const openEdit = (ext: DialplanExtension) => {
    setEditing(ext);
    setFormCtxId(ext.context_id);
    setFormExtension(ext.extension);
    setActions((ext.actions || []).map((a) => ({ priority: a.priority, type: a.application as ActionType, params: a.params })));
    setOpen(true);
  };

  const handleSave = () => {
    const data = { context_id: formCtxId, extension: formExtension, actions };
    if (editing) {
      updateMut.mutate({ id: editing.id, data }, { onSuccess: () => { setOpen(false); toast.success("Extension updated"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    } else {
      createMut.mutate(data, { onSuccess: () => { setOpen(false); toast.success("Extension created"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    }
  };

  const handleDelete = (ext: DialplanExtension) => {
    deleteMut.mutate(ext.id, { onSuccess: () => toast.success("Extension deleted"), onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
  };

  const addAction = () => {
    const maxPrio = actions.reduce((max, a) => Math.max(max, a.priority), 0);
    setActions([...actions, { priority: maxPrio + 1, type: "noop", params: { text: "" } }]);
  };

  const updateAction = (idx: number, action: ActionCreate) => {
    const next = [...actions];
    next[idx] = action;
    setActions(next);
  };

  const removeAction = (idx: number) => {
    setActions(actions.filter((_, i) => i !== idx));
  };

  const moveActionUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...actions];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setActions(next.map((a, i) => ({ ...a, priority: i + 1 })));
  };

  const moveActionDown = (idx: number) => {
    if (idx === actions.length - 1) return;
    const next = [...actions];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    setActions(next.map((a, i) => ({ ...a, priority: i + 1 })));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Extensions</h1>
          <p className="text-sm text-muted-foreground mt-1">Dialplan extensions with typed actions</p>
        </div>
        <Button onClick={openCreate} size="sm"><Plus size={14} /> Create Extension</Button>
      </div>

      <div className="flex items-center gap-3">
        <Label className="text-sm">Filter by context:</Label>
        <Select value={filterCtx} onChange={(e) => setFilterCtx(e.target.value)} className="w-64">
          <option value="">All contexts</option>
          {contexts?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
      </div>

      <DataTable columns={columns} data={filtered} isLoading={isLoading} getRowKey={(e) => e.id} onEdit={openEdit} onDelete={handleDelete} emptyMessage="No extensions configured." />

      <Dialog open={open} onClose={() => setOpen(false)} title={editing ? "Edit Extension" : "Create Extension"} className="max-w-3xl max-h-[80vh]">
        <div className="space-y-4 overflow-y-auto pr-2" style={{ maxHeight: 'calc(80vh - 120px)' }}>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="context_id">Context * <HelpIcon text="The dialplan context this extension belongs to. Only available in the context scope." /></Label>
              <Select id="context_id" value={formCtxId} onChange={(e) => setFormCtxId(e.target.value)}>
                <option value="">Select context</option>
                {contexts?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="extension">Extension * <HelpIcon text="The dialed number/pattern to match. Examples: 100 (exact), _1XX (100-199), _X. (any length), s (start extension)." /></Label>
              <Input id="extension" value={formExtension} onChange={(e) => setFormExtension(e.target.value)} placeholder="100" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Actions <HelpIcon text="Ordered list of dialplan actions executed when the extension is matched. At least one action required. Actions run in priority order." /></Label>
              <Button variant="outline" size="sm" onClick={addAction}><Plus size={12} /> Add Action</Button>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {actions.map((action, idx) => (
                <ActionForm
                  key={idx}
                  action={action}
                  onChange={(a) => updateAction(idx, a)}
                  onRemove={() => removeAction(idx)}
                  onMoveUp={() => moveActionUp(idx)}
                  onMoveDown={() => moveActionDown(idx)}
                  total={actions.length}
                  index={idx}
                  endpoints={endpointNames}
                  wsEndpoints={wsNames}
                  ariApps={ariNames}
                  contexts={contextNames}
                />
              ))}
              {actions.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No actions defined. Click "Add Action" to start.</p>}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>{editing ? "Update" : "Create"}</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}